import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import { getShelfSettings } from '../../../../../service/device/util/getDeviceMappings';
import { ComposableDevicesManagerStoreInfoResponse } from '../../../../../service/device/hooks/useDevicesManager';
import { ComposableDevicesStoreInfoResponse } from '../../../../../service/store/hooks/useDevicesOperator';
import {
  POSSIBLE_ALARMS,
  ZoneResponse,
} from '../../../../../service/store/modules/zone/ZoneService.model';
import useAlarmsByDevice from '../../../../../redux/alarm/hooks/useAlarmsByDevice';

export interface StatusOverviewByDeviceProps {
  device:
    | ComposableDevicesStoreInfoResponse
    | ComposableDevicesManagerStoreInfoResponse;
}
export default function StatusOverviewByDevice({
  device,
}: StatusOverviewByDeviceProps) {
  const shelfSettings = getShelfSettings(device);
  const shelfSettingsToUse = Object.values(shelfSettings ?? {}).filter(
    (item) => item?.shelfStatus !== undefined
  );

  const { data: alarmsData } = useAlarmsByDevice({
    device,
  });
  const mapErrorByZone = useMemo(() => {
    const shelfMappings = Array.isArray(device.shelfMappings)
      ? device.shelfMappings
      : [];

    return (
      alarmsData?.reduce((acc, current) => {
        const cuaddr = shelfMappings.find(
          (shelfMapping) => shelfMapping?.cuid === current.cu
        )?.cuaddr;
        // find key on device.composableZoneMapping Record<key, cuaddr>
        const zoneKey = Object.keys(device.composableZoneMapping ?? {}).find(
          (key) => device.composableZoneMapping[key] === cuaddr
        );
        acc[`${zoneKey}`] = current.event ?? '';

        return acc;
      }, {} as Record<string, string>) ?? {}
    );
  }, [alarmsData, device.composableZoneMapping, device.shelfMappings]);

  return (
    <Stack flexDirection={'row'} justifyContent={'center'}>
      {shelfSettingsToUse.map(
        (
          zoneSettings = {
            activeSetting: 'UNKNOWN',
            shelfStatus: { deviceOn: false, operatingMode: 'UNUSED' },
          } as ZoneResponse,
          index = 0
        ) => {
          const {
            shelfStatus: { operatingMode },
          } = zoneSettings;
          const isOff =
            zoneSettings.activeSetting === 'OFF' ||
            zoneSettings.shelfStatus.deviceOn === false;
          const unused = operatingMode === 'UNUSED';
          const warning = operatingMode === 'TRANSITIONING';
          const error =
            Boolean(mapErrorByZone[`${index + 1}`]) ||
            operatingMode === 'ERROR' ||
            operatingMode === 'ERROR_CU_MISMATCH';
          const disconnectedAlarm = Boolean(
            error &&
              zoneSettings.shelfStatus.alarms?.find(
                (alarm) => alarm === POSSIBLE_ALARMS.DISCONN
              )
          );
          const disconnected =
            !device?.deviceState?.mqttConnected || disconnectedAlarm;

          const disconnectedAndError = disconnectedAlarm
            ? mapErrorByZone[`${index + 1}`]
            : undefined;

          return (
            <Circle
              key={index}
              unused={unused}
              disconnectedAndError={disconnectedAndError}
              disconnected={disconnected}
              off={isOff}
              warning={warning}
              error={error}
            />
          );
        }
      )}
    </Stack>
  );
}

const Circle = ({
  unused,
  disconnected,
  disconnectedAndError,
  warning,
  error,
  off,
}: {
  unused?: boolean;
  warning?: boolean;
  disconnected?: boolean;
  error?: boolean;
  disconnectedAndError?: string;
  off?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <Box
      className="Zone-status"
      title={`${t('timers.status')} ${
        unused
          ? ''
          : Boolean(disconnectedAndError)
          ? t('timers.status_disconnected_and_error', {
              message: disconnectedAndError,
            })
          : disconnected
          ? t('timers.status_disconnected')
          : error
          ? t('timers.status_error')
          : off
          ? t('timers.status_off')
          : warning
          ? t('timers.status_warning')
          : t('timers.status_ok')
      }`}
      sx={{
        borderRadius: '20px',
        border: (theme) =>
          off && !error && !unused && !disconnected
            ? `2px solid ${theme.palette.mode === 'dark' ? '#FFFA' : '#000A'}`
            : undefined,
        backgroundColor: unused
          ? 'transparent'
          : disconnected
          ? 'black'
          : error
          ? 'white'
          : off
          ? 'transparent'
          : 'white',
        height: (theme) => theme.spacing(3),
        width: (theme) => theme.spacing(3),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: !unused ? 'rgba(0, 0, 0, 0.4) 0 3px 4px 0;' : undefined,
        marginX: 0.5,
      }}
    >
      <Box
        className="Zone-status"
        sx={{
          borderRadius: '10px',
          backgroundColor: unused
            ? 'transparent'
            : disconnectedAndError
            ? 'error.main'
            : disconnected
            ? 'gray'
            : error
            ? 'error.main'
            : off
            ? 'transparent'
            : warning
            ? 'warning.main'
            : 'success.main',
          height: (theme) => theme.spacing(2),
          width: (theme) => theme.spacing(2),
        }}
      ></Box>
    </Box>
  );
};
